/* eslint-disable no-console */

/**
 * Advanced ZPL Generator
 * Professional ZPL generation with templates and multiple printer support
 * kware: مولد ZPL متقدم مع قوالب ودعم طابعات متعددة
 */
class AdvancedZPLGenerator {
  constructor() {
    this.labelTemplates = this.getDefaultTemplates();
    this.printerProfiles = this.getDefaultPrinterProfiles();
    this.customTemplates = this.loadCustomTemplates();
  }

  /**
   * Generate advanced ZPL with templates and profiles
   */
  generateAdvancedZPL(items, settings) {
    const template = this.getTemplate(settings.templateId || 'zebra-standard');
    const profile = this.getPrinterProfile(settings.printerType || 'ZEBRA_ZPL');

    let zpl = this.generateZPLHeader(template, profile, settings);

    items.forEach((item, index) => {
      for (let copy = 0; copy < (settings.copies || 1); copy++) {
        zpl += this.generateItemZPL(item, template, profile, index, copy, settings);
      }
    });

    zpl += this.generateZPLFooter();

    return zpl;
  }

  /**
   * Generate ZPL header with printer-specific settings
   */
  generateZPLHeader(template, profile, settings) {
    const labelWidthDots = (template.labelWidth || 4) * profile.dpi;
    const labelHeightDots = (template.labelHeight || 2) * profile.dpi;

    let header = '^XA\n'; // Start format
    header += '^MMT\n'; // Set media type to thermal transfer
    header += `^PW${labelWidthDots}\n`; // Print width
    header += `^LL${labelHeightDots}\n`; // Label length
    header += '^LS0\n'; // Label shift (0 = no shift)

    // Set print speed if specified
    if (settings.printSpeed) {
      header += `^PR${settings.printSpeed}\n`;
    }

    // Set print density if specified
    if (settings.printDensity) {
      header += `^PD${settings.printDensity}\n`;
    }

    return header;
  }

  /**
   * Generate ZPL for individual item
   */
  generateItemZPL(item, template, profile, index, copy, settings) {
    const position = this.calculateItemPosition(index, copy, template, profile, settings);
    let itemZPL = '';

    // Label border if enabled
    if (template.border && template.border.enabled) {
      itemZPL += this.generateBorderZPL(position, template.border);
    }

    // Barcode
    itemZPL += this.generateBarcodeZPL(
      item,
      position.barcode,
      template.barcode,
      settings.format || 'CODE128'
    );

    // Text elements
    if (template.includeText || settings.includeText) {
      itemZPL += this.generateTextElements(item, position.text, template.text);
    }

    // Logo if specified
    if (template.logo && template.logo.enabled) {
      itemZPL += this.generateLogoZPL(position.logo, template.logo);
    }

    return itemZPL;
  }

  /**
   * Generate barcode ZPL with format support
   */
  generateBarcodeZPL(item, position, barcodeConfig, format) {
    if (!item.barcode) return '';

    const { x, y } = position;
    const config = { ...this.getDefaultBarcodeConfig(), ...barcodeConfig };

    let zpl = `^FO${x},${y}`;

    // Set barcode width and ratio
    zpl += `^BY${config.moduleWidth},${config.ratio},${config.height}`;

    // Generate specific barcode type
    switch (format.toUpperCase()) {
      case 'CODE128':
        zpl += `^BCN,,${config.showText ? 'Y' : 'N'},N`;
        break;
      case 'CODE39':
        zpl += `^B3N,N,${config.height},${config.showText ? 'Y' : 'N'},N`;
        break;
      case 'EAN13':
        zpl += `^BEN,${config.height},${config.showText ? 'Y' : 'N'},N`;
        break;
      case 'EAN8':
        zpl += `^B8N,${config.height},${config.showText ? 'Y' : 'N'},N`;
        break;
      case 'UPC-A':
        zpl += `^BUN,${config.height},${config.showText ? 'Y' : 'N'},N`;
        break;
      case 'UPC-E':
        zpl += `^B9N,${config.height},${config.showText ? 'Y' : 'N'},N`;
        break;
      default:
        zpl += `^BCN,,${config.showText ? 'Y' : 'N'},N`;
    }

    zpl += `^FD${this.escapeZPL(item.barcode)}^FS\n`;

    return zpl;
  }

  /**
   * Generate text elements ZPL
   */
  generateTextElements(item, textPositions, textConfig) {
    let zpl = '';
    const config = { ...this.getDefaultTextConfig(), ...textConfig };

    textPositions.forEach(pos => {
      const value = this.getFieldValue(item, pos.field);
      if (value) {
        zpl += `^FO${pos.x},${pos.y}`;
        zpl += `^A${config.font || '0'}N,${config.fontSize || 25},${config.fontWidth || 25}`;
        zpl += '^FH\\'; // Enable hex encoding
        zpl += `^FD${this.escapeZPL(value)}^FS\n`;
      }
    });

    return zpl;
  }

  /**
   * Generate border ZPL
   */
  generateBorderZPL(position, borderConfig) {
    const { x, y, width, height } = position.label;
    const thickness = borderConfig.thickness || 2;

    let zpl = '';

    // Top border
    zpl += `^FO${x},${y}^GB${width},${thickness},${thickness}^FS\n`;
    // Bottom border
    zpl += `^FO${x},${y + height - thickness}^GB${width},${thickness},${thickness}^FS\n`;
    // Left border
    zpl += `^FO${x},${y}^GB${thickness},${height},${thickness}^FS\n`;
    // Right border
    zpl += `^FO${x + width - thickness},${y}^GB${thickness},${height},${thickness}^FS\n`;

    return zpl;
  }

  /**
   * Generate logo ZPL (placeholder for future implementation)
   */
  generateLogoZPL(position, logoConfig) {
    // Future: Support for logo embedding
    return `^FO${position.x},${position.y}^A0N,20,20^FD[LOGO]^FS\n`;
  }

  /**
   * Calculate item position on label
   */
  calculateItemPosition(index, copy, template, profile) {
    const labelWidthDots = template.labelWidth * profile.dpi;
    const labelHeightDots = template.labelHeight * profile.dpi;

    // For now, single item per label
    const baseX = template.margin || 50;
    const baseY = template.margin || 50;

    return {
      label: {
        x: baseX,
        y: baseY,
        width: labelWidthDots - (baseX * 2),
        height: labelHeightDots - (baseY * 2)
      },
      barcode: {
        x: baseX + (template.barcode?.offsetX || 0),
        y: baseY + (template.barcode?.offsetY || 0)
      },
      text: template.text?.positions?.map(pos => ({
        x: baseX + pos.x,
        y: baseY + pos.y,
        field: pos.field
      })) || [],
      logo: {
        x: baseX + (template.logo?.offsetX || 0),
        y: baseY + (template.logo?.offsetY || 0)
      }
    };
  }

  /**
   * Get field value from item
   */
  getFieldValue(item, field) {
    switch (field) {
      case 'barcode':
        return item.barcode;
      case 'materialType':
        return item.materialType?.name;
      case 'location':
        return item.effectiveLocation?.name;
      case 'callNumber':
        return item.callNumber || item.effectiveCallNumberComponents?.callNumber;
      case 'title':
        return item.title;
      case 'volume':
        return item.volume;
      case 'enumeration':
        return item.enumeration;
      case 'chronology':
        return item.chronology;
      default:
        return item[field];
    }
  }

  /**
   * Generate ZPL footer
   */
  generateZPLFooter() {
    return '^XZ\n'; // End format
  }

  /**
   * Escape special characters for ZPL
   */
  escapeZPL(text) {
    if (!text) return '';

    return text
      .replace(/\\/g, '\\\\')  // Escape backslashes
      .replace(/\^/g, '\\^')   // Escape carets
      .replace(/~/g, '\\~')    // Escape tildes
      .replace(/\n/g, '\\&')   // Replace newlines
      .replace(/"/g, '\\"');   // Escape quotes
  }

  /**
   * Get template by ID
   */
  getTemplate(templateId) {
    return this.labelTemplates[templateId] ||
           this.customTemplates[templateId] ||
           this.labelTemplates['zebra-standard'];
  }

  /**
   * Get printer profile by type
   */
  getPrinterProfile(printerType) {
    return this.printerProfiles[printerType] || this.printerProfiles.ZEBRA_ZPL;
  }

  /**
   * Get default label templates
   */
  getDefaultTemplates() {
    return {
      'zebra-standard': {
        name: 'Zebra Standard 4x2',
        labelWidth: 4,
        labelHeight: 2,
        margin: 50,
        barcode: {
          offsetX: 0,
          offsetY: 0,
          moduleWidth: 3,
          ratio: 2,
          height: 100,
          showText: false
        },
        text: {
          font: '0',
          fontSize: 25,
          fontWidth: 25,
          positions: [
            { x: 0, y: 120, field: 'barcode' },
            { x: 0, y: 160, field: 'materialType' },
            { x: 0, y: 200, field: 'location' },
            { x: 0, y: 240, field: 'callNumber' }
          ]
        },
        includeText: true,
        border: { enabled: false, thickness: 2 },
        logo: { enabled: false }
      },

      'zebra-compact': {
        name: 'Zebra Compact 2x1',
        labelWidth: 2,
        labelHeight: 1,
        margin: 25,
        barcode: {
          offsetX: 0,
          offsetY: 0,
          moduleWidth: 2,
          ratio: 2,
          height: 60,
          showText: false
        },
        text: {
          font: '0',
          fontSize: 15,
          fontWidth: 15,
          positions: [
            { x: 0, y: 75, field: 'barcode' }
          ]
        },
        includeText: true,
        border: { enabled: false },
        logo: { enabled: false }
      },

      'zebra-detailed': {
        name: 'Zebra Detailed 4x3',
        labelWidth: 4,
        labelHeight: 3,
        margin: 50,
        barcode: {
          offsetX: 0,
          offsetY: 0,
          moduleWidth: 3,
          ratio: 2,
          height: 100,
          showText: false
        },
        text: {
          font: '0',
          fontSize: 20,
          fontWidth: 20,
          positions: [
            { x: 0, y: 120, field: 'barcode' },
            { x: 0, y: 150, field: 'title' },
            { x: 0, y: 180, field: 'materialType' },
            { x: 0, y: 210, field: 'location' },
            { x: 0, y: 240, field: 'callNumber' },
            { x: 0, y: 270, field: 'volume' }
          ]
        },
        includeText: true,
        border: { enabled: true, thickness: 2 },
        logo: { enabled: false }
      }
    };
  }

  /**
   * Get default printer profiles
   */
  getDefaultPrinterProfiles() {
    return {
      'ZEBRA_ZPL': {
        name: 'Zebra ZPL Printers',
        dpi: 203,
        maxWidth: 4.0,
        maxHeight: 6.0,
        supportedFormats: ['CODE128', 'CODE39', 'EAN13', 'EAN8', 'UPC-A', 'UPC-E'],
        speedRange: [2, 14],
        densityRange: [1, 30]
      },

      'ZEBRA_EPL': {
        name: 'Zebra EPL Printers',
        dpi: 203,
        maxWidth: 4.0,
        maxHeight: 6.0,
        supportedFormats: ['CODE128', 'CODE39', 'EAN13'],
        speedRange: [1, 5],
        densityRange: [1, 15]
      },

      'ESC_POS': {
        name: 'ESC/POS Thermal Printers',
        dpi: 180,
        maxWidth: 3.2,
        maxHeight: 8.0,
        supportedFormats: ['CODE128', 'CODE39', 'EAN13'],
        speedRange: [1, 8],
        densityRange: [1, 20]
      }
    };
  }

  /**
   * Get default barcode configuration
   */
  getDefaultBarcodeConfig() {
    return {
      moduleWidth: 3,
      ratio: 2,
      height: 100,
      showText: false
    };
  }

  /**
   * Get default text configuration
   */
  getDefaultTextConfig() {
    return {
      font: '0',
      fontSize: 25,
      fontWidth: 25
    };
  }

  /**
   * Load custom templates from localStorage
   */
  loadCustomTemplates() {
    try {
      const stored = localStorage.getItem('folio-zpl-templates');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load custom templates:', error);
      return {};
    }
  }

  /**
   * Save custom template
   */
  saveCustomTemplate(templateId, template) {
    try {
      this.customTemplates[templateId] = template;
      localStorage.setItem('folio-zpl-templates', JSON.stringify(this.customTemplates));
    } catch (error) {
      console.error('Failed to save custom template:', error);
    }
  }

  /**
   * Get all available templates
   */
  getAllTemplates() {
    return {
      ...this.labelTemplates,
      ...this.customTemplates
    };
  }

  /**
   * Get template options for UI
   */
  getTemplateOptions() {
    const allTemplates = this.getAllTemplates();

    return Object.keys(allTemplates).map(id => ({
      value: id,
      label: allTemplates[id].name,
      description: `${allTemplates[id].labelWidth}"×${allTemplates[id].labelHeight}"`
    }));
  }

  /**
   * Get printer profile options for UI
   */
  getPrinterProfileOptions() {
    return Object.keys(this.printerProfiles).map(type => ({
      value: type,
      label: this.printerProfiles[type].name,
      description: `${this.printerProfiles[type].dpi} DPI`
    }));
  }

  /**
   * Validate ZPL output
   */
  validateZPL(zpl) {
    const errors = [];

    if (!zpl.startsWith('^XA')) {
      errors.push('ZPL must start with ^XA');
    }

    if (!zpl.endsWith('^XZ\n')) {
      errors.push('ZPL must end with ^XZ');
    }

    // Check for balanced field commands
    const fdCount = (zpl.match(/\^FD/g) || []).length;
    const fsCount = (zpl.match(/\^FS/g) || []).length;

    if (fdCount !== fsCount) {
      errors.push('Unbalanced ^FD and ^FS commands');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

export default AdvancedZPLGenerator;
